import {
  IMarketPriceDatasetMetadata,
  MarketPriceSetting,
} from "types/marketPrice";

/**
 * CLE-2939 — the settings a competitor's market price can apply to, in the API enum's order
 * (`MarketPriceSetting` in services/database/schema.prisma). The SuperAdmin upload pages and the
 * Smart Insights Market Price Analysis sub-tabs both read these, so the two cannot name a setting
 * differently.
 */
export const MARKET_PRICE_SETTINGS: MarketPriceSetting[] = [
  "HOSPITAL",
  "ASC",
  "PHYSICIAN_OFFICE",
  "PHYSICIAN_FACILITY",
];

export const MARKET_PRICE_SETTING_LABELS: Record<MarketPriceSetting, string> = {
  HOSPITAL: "Hospital",
  ASC: "Surgery center",
  PHYSICIAN_OFFICE: "Physician office",
  PHYSICIAN_FACILITY: "Physician facility",
};

/** Dropdown options in enum order. */
export const MARKET_PRICE_SETTING_OPTIONS: {
  label: string;
  value: MarketPriceSetting;
}[] = MARKET_PRICE_SETTINGS.map((setting) => ({
  label: MARKET_PRICE_SETTING_LABELS[setting],
  value: setting,
}));

/** The label for a setting; a value this build does not know is shown as-is rather than blank. */
export function getMarketPriceSettingLabel(
  setting: string | null | undefined
): string {
  if (!setting) return "";
  return MARKET_PRICE_SETTING_LABELS[setting as MarketPriceSetting] ?? setting;
}

/**
 * A dataset's settings for the SuperAdmin pages, e.g. ["Hospital (37,784)", "Surgery center (12)"],
 * in enum order. Datasets uploaded before CLE-2939 have no `rowsBySetting`: they fall back to the
 * default setting's label without a count, and to an empty list when that is missing too.
 */
export function describeDatasetSettings(
  metadata: IMarketPriceDatasetMetadata | null | undefined
): string[] {
  const rowsBySetting = metadata?.rowsBySetting;
  if (rowsBySetting && Object.keys(rowsBySetting).length > 0) {
    return MARKET_PRICE_SETTINGS.filter(
      (setting) => (rowsBySetting[setting] ?? 0) > 0
    ).map(
      (setting) =>
        `${MARKET_PRICE_SETTING_LABELS[setting]} (${(
          rowsBySetting[setting] ?? 0
        ).toLocaleString("en-US")})`
    );
  }
  if (metadata?.defaultSetting) {
    return [getMarketPriceSettingLabel(metadata.defaultSetting)];
  }
  return [];
}
